/**
 * UserSearchInput — reusable typeahead for finding platform users.
 *
 * Searches by display name, screen name (@handle), and email.
 * Filters out the current user and any already-selected users.
 * Uses the cached SecurityUsers data (already loaded by useApiSecurityData).
 */
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { SecurityUserResponse } from '../../api/security';
import { mediaSrc } from '../../api/mediaUrl';
import styles from './Chat.module.css';

interface UserSearchInputProps {
  /** All platform users (from useApiSecurityData or fetchAllSecurityUsers) */
  users: SecurityUserResponse[];
  /** GUIDs to exclude from results (current user, already-selected, etc.) */
  excludeGuids?: string[];
  /** Called when a user is selected from the dropdown */
  onSelect: (user: SecurityUserResponse) => void;
  placeholder?: string;
}

export default function UserSearchInput({
  users,
  excludeGuids = [],
  onSelect,
  placeholder,
}: UserSearchInputProps) {
  const { t } = useAppTranslation('chat');
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && e.target instanceof Node && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Exclude set for fast lookup (case-insensitive)
  const excludeSet = useMemo(() => {
    return new Set(excludeGuids.map((g) => g.toUpperCase()));
  }, [excludeGuids]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    return users
      .filter((u) => {
        if (!u.isActive) return false;
        if (excludeSet.has(u.userGuid.toUpperCase())) return false;

        const displayName = (u.displayName || u.name || '').toLowerCase();
        const screenName = (u.screenName || '').toLowerCase();
        const email = (u.email || '').toLowerCase();

        return displayName.includes(q)
          || screenName.includes(q)
          || email.includes(q);
      })
      .slice(0, 8); // Limit to 8 results for performance
  }, [query, users, excludeSet]);

  const handleSelect = useCallback((user: SecurityUserResponse) => {
    onSelect(user);
    setQuery('');
    setIsOpen(false);
  }, [onSelect]);

  return (
    <div className={styles.userSearchContainer} ref={containerRef}>
      <input
        type="text"
        className={styles.userSearchInput}
        placeholder={placeholder || t('create.selectContacts')}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => { if (query.trim()) setIsOpen(true); }}
      />

      {isOpen && results.length > 0 && (
        <div className={styles.userSearchDropdown}>
          {results.map((user) => (
            <button
              key={user.userGuid}
              type="button"
              className={styles.userSearchResult}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur before click fires
                handleSelect(user);
              }}
            >
              <div className={styles.userSearchAvatar}>
                {user.profileImageUrl ? (
                  <img
                    src={mediaSrc(user.profileImageUrl) ?? undefined}
                    alt=""
                    className={styles.userSearchAvatarImg}
                  />
                ) : (
                  <span className={styles.userSearchAvatarInitial}>
                    {(user.displayName || user.name || '?')[0].toUpperCase()}
                  </span>
                )}
              </div>
              <div className={styles.userSearchInfo}>
                <span className={styles.userSearchName}>
                  {user.displayName || user.name}
                </span>
                {user.screenName && (
                  <span className={styles.userSearchHandle}>@{user.screenName}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {isOpen && query.trim() && results.length === 0 && (
        <div className={styles.userSearchDropdown}>
          <div className={styles.userSearchNoResults}>
            {t('search.noResults')}
          </div>
        </div>
      )}
    </div>
  );
}
